import { GameState, GameSettings, SessionData } from '../types';
import { STORAGE_KEY } from '../constants/storageKeys';
import { SCHEMA_VERSION } from '../constants/defaults';
import { validateStateStructure } from '../utils/validators';
import { Manifest } from '../types/manifest';

type StorageBackend = 'localStorage' | 'sessionStorage' | 'memory';

export class StatePersistence {
  private storageType: StorageBackend = 'localStorage';
  private memoryStore: Map<string, string> = new Map();

  constructor() {
    this.detectStorage();
  }

  save(state: GameState): boolean {
    try {
      state.timestamp = new Date().toISOString();
      const json = JSON.stringify(state);
      this.setItem(STORAGE_KEY, json);
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        return this.handleQuotaExceeded(state);
      }
      console.error('[StatePersistence] Failed to save state:', error);
      return false;
    }
  }

  load(): GameState | null {
    try {
      const json = this.getItem(STORAGE_KEY);
      if (!json) return null;

      const data = JSON.parse(json);

      // Handle old versions
      if (data.version && data.version < SCHEMA_VERSION) {
        return this.migrateState(data);
      }

      // Validate structure
      if (!validateStateStructure(data)) {
        console.warn('[StatePersistence] Invalid state structure, resetting');
        this.clear();
        return null;
      }

      // Gracefully handle missing startTime (per assumption A6)
      if (data.session && data.session.startTime === undefined) {
        data.session.startTime = null;
      }

      // Gracefully handle missing uiLocale
      if (data.settings && data.settings.uiLocale === undefined) {
        data.settings.uiLocale = 'vi';
      }

      return data as GameState;
    } catch (error) {
      console.error('[StatePersistence] Failed to load state:', error);
      this.clear();
      return null;
    }
  }

  clear(): void {
    try {
      this.removeItem(STORAGE_KEY);
    } catch {
      // Ignore
    }
  }

  isAvailable(): boolean {
    return this.storageType !== 'memory';
  }

  getStorageType(): StorageBackend {
    return this.storageType;
  }

  getDefaultState(manifest: Manifest): GameState {
    return {
      version: SCHEMA_VERSION,
      timestamp: new Date().toISOString(),
      gamePhase: 'IDLE',
      settings: {
        language: manifest.defaults.language,
        style: manifest.defaults.style,
        min: manifest.defaults.min,
        max: manifest.defaults.max,
        autoPlayEnabled: false,
        autoPlayInterval: manifest.defaults.autoPlayInterval,
        uiLocale: 'vi',
      },
      session: {
        calledNumbers: [],
        currentNumber: null,
        shuffledQueue: [],
        startTime: null,
      },
    };
  }

  private migrateState(oldState: Record<string, unknown>): GameState | null {
    try {
      const migrated: GameState = {
        version: SCHEMA_VERSION,
        timestamp: new Date().toISOString(),
        gamePhase: 'IDLE',
        settings: {
          language: (oldState.selectedLanguage as string) || (oldState.settings as Record<string, unknown>)?.language as string || 'vi',
          style: (oldState.selectedStyle as string) || (oldState.settings as Record<string, unknown>)?.style as string || 'tet_traditional',
          min: (oldState.minRange as number) || (oldState.settings as Record<string, unknown>)?.min as number || 1,
          max: (oldState.maxRange as number) || (oldState.settings as Record<string, unknown>)?.max as number || 99,
          autoPlayEnabled: false,
          autoPlayInterval: 5,
          uiLocale: 'vi',
        },
        session: {
          calledNumbers: Array.isArray(oldState.calledNumbers)
            ? oldState.calledNumbers as number[]
            : Array.isArray((oldState.session as Record<string, unknown>)?.calledNumbers)
              ? (oldState.session as Record<string, unknown>).calledNumbers as number[]
              : [],
          currentNumber: null,
          shuffledQueue: [],
          startTime: null,
        },
      };

      if (validateStateStructure(migrated)) {
        return migrated;
      }
    } catch (error) {
      console.error('[StatePersistence] Migration failed:', error);
    }
    return null;
  }

  private handleQuotaExceeded(state: GameState): boolean {
    if (state.session.calledNumbers.length > 1000) {
      state.session.calledNumbers = state.session.calledNumbers.slice(-1000);
      try {
        const json = JSON.stringify(state);
        this.setItem(STORAGE_KEY, json);
        return true;
      } catch {
        console.error('[StatePersistence] Still over quota after trimming');
      }
    }
    return false;
  }

  private detectStorage(): void {
    if (this.testStorage('localStorage')) {
      this.storageType = 'localStorage';
    } else if (this.testStorage('sessionStorage')) {
      this.storageType = 'sessionStorage';
      console.warn('[StatePersistence] Falling back to sessionStorage');
    } else {
      this.storageType = 'memory';
      console.warn('[StatePersistence] Falling back to in-memory storage');
    }
  }

  private testStorage(type: 'localStorage' | 'sessionStorage'): boolean {
    try {
      const storage = window[type];
      const testKey = '__storage_test__';
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private getItem(key: string): string | null {
    switch (this.storageType) {
      case 'localStorage':
        return localStorage.getItem(key);
      case 'sessionStorage':
        return sessionStorage.getItem(key);
      case 'memory':
        return this.memoryStore.get(key) ?? null;
    }
  }

  private setItem(key: string, value: string): void {
    switch (this.storageType) {
      case 'localStorage':
        localStorage.setItem(key, value);
        break;
      case 'sessionStorage':
        sessionStorage.setItem(key, value);
        break;
      case 'memory':
        this.memoryStore.set(key, value);
        break;
    }
  }

  private removeItem(key: string): void {
    switch (this.storageType) {
      case 'localStorage':
        localStorage.removeItem(key);
        break;
      case 'sessionStorage':
        sessionStorage.removeItem(key);
        break;
      case 'memory':
        this.memoryStore.delete(key);
        break;
    }
  }
}
